import { Badge, formatDateTime, MoneyText, orderStatusTone, type Column } from '@platform/design-system';
import type { ShippingClient } from '@platform/openapi';
import { Printer } from 'lucide-react';
import { useCarriers } from '../settings/carriers.ts';
import { CarrierStatus } from './CarrierStatus.tsx';
import { usePageContext } from '../usePageContext.ts';
import { amountOf, type Order } from './orders.ts';
import styles from './Orders.module.css';
import { TrackingCode } from '../TrackingCode.tsx';

const CARRIER_TONES = 5;

const carrierToneOf = (code: string): string => {
  const codeUnits = Array.from({ length: code.length }, (_unused, index) => code.charCodeAt(index));
  return String(codeUnits.reduce((total, unit) => total + unit, 0) % CARRIER_TONES);
};

const itemCountOf = (order: Order): number => order.lines.reduce((total, line) => total + line.quantity, 0);

export const useOrderColumns = (
  shipping: ShippingClient,
  geoNames: (code: string) => string,
): readonly Column<Order>[] => {
  const { t, locale } = usePageContext();
  const carriers = useCarriers(shipping).data?.items ?? [];
  const carrierNameOf = (code: string) => carriers.find((carrier) => carrier.code === code)?.name ?? code;
  return [
    {
      id: 'order',
      header: t('orders.column_order'),
      primary: true,
      cell: (order) => (
        <span className={[styles['stacked'], styles['orderCell']].join(' ')}>
          <span className={styles['reference']}>{order.number}</span>
          <span className={styles['muted']}>{formatDateTime(order.createdAt, locale)}</span>
        </span>
      ),
    },
    {
      id: 'customer',
      header: t('orders.customer'),
      cell: (order) => (
        <span className={styles['stacked']}>
          <span>{order.customer.name}</span>
          <span className={[styles['muted'], styles['phone']].join(' ')}>{order.customer.phone}</span>
        </span>
      ),
    },
    {
      id: 'destination',
      header: t('orders.destination'),
      cell: (order) => geoNames(order.shipping.governorate),
    },
    {
      id: 'items',
      header: t('orders.column_items'),
      cell: (order) => t('orders.itemCount', { count: itemCountOf(order) }),
    },
    {
      id: 'cod',
      header: t('orders.column_cod'),
      numeric: true,
      cell: (order) => <MoneyText money={amountOf(order, order.totals.codAmount)} locale={locale} />,
    },
    {
      id: 'carrier',
      header: t('orders.column_carrier'),
      cell: (order) =>
        order.dispatch.carrierCode === null ? (
          <span className={styles['carrier']}>
            <span className={styles['carrierMark']} aria-hidden="true">
              ·
            </span>
            <span className={styles['muted']}>{t('orders.carrierUnassigned')}</span>
          </span>
        ) : (
          <span className={styles['carrier']}>
            <span
              className={styles['carrierMark']}
              data-tone={carrierToneOf(order.dispatch.carrierCode)}
              aria-hidden="true"
            >
              {carrierNameOf(order.dispatch.carrierCode).charAt(0).toUpperCase()}
            </span>
            {carrierNameOf(order.dispatch.carrierCode)}
          </span>
        ),
    },
    {
      id: 'barcode',
      header: t('orders.column_barcode'),
      cell: (order) =>
        order.dispatch.trackingNumber === null ? (
          <span className={styles['muted']}>{t('orders.barcodeNone')}</span>
        ) : (
          <TrackingCode code={order.dispatch.trackingNumber} />
        ),
    },
    {
      id: 'status',
      status: true,
      header: t('orders.status'),
      cell: (order) => (
        <span className={styles['statusCell']}>
          <Badge tone={orderStatusTone[order.status]}>{t(`orders.status_${order.status}`)}</Badge>
          {order.dispatch.labelPrintedAt === null ? null : (
            <Printer size={16} aria-label={t('orders.labelPrinted')} className={styles['muted']} />
          )}
        </span>
      ),
    },
    {
      id: 'carrierStatus',
      header: t('orders.carrierStatus'),
      cell: (order) => <CarrierStatus dispatch={order.dispatch} />,
    },
  ];
};
